import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, X, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { QAFindingResponseDialog } from './qa/QAFindingResponseDialog';
import {
  useNotificationsState,
  useNotificationsActions,
} from '../../context/NotificationsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * NotificationBell - Self-contained bell icon + unread badge + dropdown
 *
 * Extracted from App.js (Feb 2026) to:
 *  - Isolate the heavy notification dropdown JSX (~530 lines) from the
 *    main Dashboard render tree.
 *  - Wrap with React.memo so unrelated Dashboard state changes (tab
 *    switches, dialog opens) don't force the bell to re-render.
 *  - Set up the structure so a follow-up refactor can move notification
 *    polling/state out of the global UIContext entirely.
 *
 * Mirrors the MessagesDropdown pattern: state + setters + navigation
 * callbacks are passed in as props. The bell never directly reads from
 * UIContext.
 */
const NotificationBellComponent = ({
  // Open the "View All" dialog (Dashboard-owned local state)
  setShowNotificationManager,

  // Navigation / detail setters used by the per-notification action buttons
  setEditingAppointment,
  setShowAppointmentForm,
  changeTab,
  setActiveTab,
  setSelectedChangeRequest,
  setShowChangeRequestDetail,
  setSelectedTask,
  setShowTaskDetail,
  setSelectedClient,
  setShowClientDetail,
  setClientDetailTab,
  setPreviewClient,
  setShowClientPreview,
  setSelectedNote,
  setShowNoteDetail,
  setSelectedReferralForDetails,
}) => {
  // Subscribe to notifications data + actions directly from context.
  // Dashboard (the parent) no longer touches this context, so polls
  // re-render only this bell + the manager dialog.
  const { notifications, unreadCount, showNotifications } = useNotificationsState();
  const { setShowNotifications, fetchNotifications, dismissNotification } = useNotificationsActions();
  // Close the dropdown when the user clicks anywhere outside it.
  // Uses mousedown so the close fires before any click handler on the
  // outside target (e.g. clicking the messages dropdown shouldn't
  // leave the notifications panel open behind it).
  const wrapperRef = useRef(null);
  // Jun 2026 — QA finding respond/verify dialog (related_id "review:section")
  const [qaFindingRef, setQaFindingRef] = React.useState(null);
  useEffect(() => {
    if (!showNotifications) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showNotifications, setShowNotifications]);

  return (
    <div className="relative" data-testid="notification-bell-wrapper" ref={wrapperRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative"
        data-testid="notification-bell-button"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto"
          data-testid="notification-dropdown"
        >
          <div className="p-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowNotifications(false);
                  setShowNotificationManager(true);
                }}
                data-testid="notification-view-all-button"
              >
                <Maximize2 className="h-3 w-3 mr-1" />
                View All
              </Button>
              {notifications.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="notification-dismiss-all-button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/notifications/dismiss-all`);
                      fetchNotifications();
                      toast.success('All notifications dismissed');
                    } catch (error) {
                      toast.error('Failed to dismiss notifications');
                    }
                  }}
                >
                  Dismiss All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(false)}
                data-testid="notification-close-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="notification-empty-state">
                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No new notifications</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 border-b hover:bg-gray-50 ${!notif.read ? 'bg-blue-50' : ''}`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-semibold text-sm">{notif.title}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissNotification(notif.id)}
                      className="h-6 w-6 p-0"
                      data-testid={`notification-dismiss-${notif.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-sm text-gray-700">{notif.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                  {notif.related_type === 'qa_finding' && String(notif.related_id || '').includes(':') && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1"
                      data-testid={`notification-open-qa-finding-${notif.id}`}
                      onClick={async () => {
                        const [rid, sid] = String(notif.related_id).split(':');
                        setQaFindingRef({ reviewId: rid, sectionId: sid });
                        setShowNotifications(false);
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch { /* non-blocking */ }
                      }}
                    >
                      Open QA Finding
                    </Button>
                  )}
                  {notif.appointment_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1"
                      data-testid={`notification-view-appointment-${notif.id}`}
                      onClick={async () => {
                        try {
                          // Feb 2026 perf fix: was fetching the entire
                          // ±30d appointments list to find one by id;
                          // now hit the single-appointment endpoint
                          // directly.
                          const response = await axios.get(`${API}/appointments/${notif.appointment_id}`);
                          const appointment = response.data;

                          if (appointment) {
                            setEditingAppointment(appointment);
                            setShowAppointmentForm(true);
                            changeTab('appointments');
                            setShowNotifications(false);

                            await axios.post(`${API}/notifications/${notif.id}/read`);
                            fetchNotifications();
                          } else {
                            console.error('Appointment not found. Notification appointment_id:', notif.appointment_id);
                            toast.error('Appointment not found in your accessible appointments');
                          }
                        } catch (err) {
                          console.error('Error fetching appointment:', err);
                          toast.error('Failed to load appointment');
                        }
                      }}
                    >
                      View Appointment
                    </Button>
                  )}
                  {notif.type === 'password_reset_request' && notif.password_reset_request_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-blue-600"
                      data-testid={`notification-view-password-reset-${notif.id}`}
                      onClick={async () => {
                        setActiveTab('admin');
                        setShowNotifications(false);
                        setTimeout(() => {
                          const element = document.querySelector('[data-password-resets]');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch (err) {
                          console.error('Error marking notification as read:', err);
                        }
                      }}
                    >
                      View Password Reset Request
                    </Button>
                  )}
                  {notif.type === 'medical_records_request' && notif.medical_records_request_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-blue-600"
                      data-testid={`notification-view-med-records-${notif.id}`}
                      onClick={async () => {
                        changeTab('medical-records');
                        setShowNotifications(false);
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch (err) {
                          console.error('Error marking notification as read:', err);
                        }
                      }}
                    >
                      View Medical Records Request
                    </Button>
                  )}
                  {notif.type === 'era_received' && notif.data?.era_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-teal-600 font-semibold"
                      data-testid={`notification-open-era-${notif.id}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                        } catch { /* best-effort */ }
                        setShowNotifications(false);
                        window.location.href = `/v2/era?eraId=${encodeURIComponent(notif.data.era_id)}`;
                      }}
                    >
                      Open ERA
                    </Button>
                  )}
                  {notif.type === 'statement_payment_received' && notif.data?.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-emerald-600 font-semibold"
                      data-testid={`notification-payment-apply-${notif.id}`}
                      onClick={async () => {
                        try {
                          const clientResponse = await axios.get(`${API}/clients/${notif.data.client_id}`, {
                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                          });
                          setSelectedClient(clientResponse.data);
                          setClientDetailTab('billing');
                          setShowClientDetail(true);
                          setShowNotifications(false);
                          await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                          });
                          fetchNotifications();
                          toast.success('Opening billing chart to apply the payment');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to open billing chart');
                        }
                      }}
                    >
                      Open Billing Chart to Apply Payment
                    </Button>
                  )}
                  {notif.type === 'square_payment_auto_matched' && notif.data?.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-emerald-600 font-semibold"
                      data-testid={`notification-automatch-open-billing-${notif.id}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                        } catch { /* best-effort */ }
                        setShowNotifications(false);
                        window.location.href = `/v2/clients/${encodeURIComponent(notif.data.client_id)}?tab=billing`;
                      }}
                    >
                      Open Client Billing Tab
                    </Button>
                  )}
                  {notif.type === 'square_payment_unmatched' && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-amber-600 font-semibold"
                      data-testid={`notification-unmatched-open-queue-${notif.id}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                        } catch { /* best-effort */ }
                        setShowNotifications(false);
                        window.location.href = notif.link || '/v2/client-bills';
                      }}
                    >
                      Open Unmatched Payments
                    </Button>
                  )}
                  {notif.type === 'treatment_plan_signature' && notif.data?.plan_id && notif.data?.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-purple-600 font-semibold"
                      data-testid={`notification-tp-signature-${notif.id}`}
                      onClick={async () => {
                        try {
                          const clientResponse = await axios.get(`${API}/clients/${notif.data.client_id}`, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          setSelectedClient(clientResponse.data);
                          setClientDetailTab('treatment-plans');
                          setShowClientDetail(true);
                          setShowNotifications(false);

                          await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          fetchNotifications();

                          toast.success('Opening treatment plan for signature');
                        } catch (err) {
                          console.error('Error opening treatment plan:', err);
                          console.error('Error response:', err.response);
                          console.error('Error status:', err.response?.status);
                          console.error('Error detail:', err.response?.data);
                          toast.error(err.response?.data?.detail || 'Failed to load treatment plan');
                        }
                      }}
                    >
                      Review &amp; Sign Treatment Plan
                    </Button>
                  )}
                  {notif.type === 'treatment_plan_due' && notif.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-blue-600 font-semibold"
                      data-testid={`notification-tp-due-${notif.id}`}
                      onClick={async () => {
                        try {
                          const clientResponse = await axios.get(`${API}/clients/${notif.client_id}`, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          setSelectedClient(clientResponse.data);
                          setClientDetailTab('treatment-plans');
                          setShowClientDetail(true);
                          setShowNotifications(false);

                          await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          fetchNotifications();

                          toast.info('Opening client treatment plans - Create Major Review for updates');
                        } catch (err) {
                          console.error('Error opening treatment plan:', err);
                          toast.error('Failed to load client information');
                        }
                      }}
                    >
                      Review Treatment Plan
                    </Button>
                  )}
                  {notif.type === 'treatment_plan_corrections' && notif.data?.plan_id && notif.data?.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-orange-600 font-semibold"
                      data-testid={`notification-tp-corrections-${notif.id}`}
                      onClick={async () => {
                        try {
                          const clientResponse = await axios.get(`${API}/clients/${notif.data.client_id}`, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          setSelectedClient(clientResponse.data);
                          setClientDetailTab('treatment-plans');
                          setShowClientDetail(true);
                          setActiveTab('clients');
                          setShowNotifications(false);

                          await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
                            headers: {
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                          });
                          fetchNotifications();

                          toast.success('Opening treatment plan for corrections');
                        } catch (err) {
                          console.error('Error opening treatment plan:', err);
                          toast.error('Failed to load treatment plan');
                        }
                      }}
                    >
                      Make Corrections
                    </Button>
                  )}
                  {/* Change Request Notifications */}
                  {(notif.type === 'change_request_submitted' ||
                    notif.type === 'change_request_approved' ||
                    notif.type === 'change_request_denied' ||
                    notif.type?.includes('change_request')) &&
                   (notif.change_request_id || notif.reference_id) && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-blue-600 font-semibold"
                      data-testid={`notification-change-request-${notif.id}`}
                      onClick={async () => {
                        try {
                          const requestId = notif.change_request_id || notif.reference_id;
                          const response = await axios.get(`${API}/change-requests/${requestId}`);
                          setSelectedChangeRequest(response.data);
                          setShowChangeRequestDetail(true);
                          setShowNotifications(false);

                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch (err) {
                          console.error('Error opening change request:', err);
                          toast.error('Failed to load change request');
                        }
                      }}
                    >
                      View Change Request
                    </Button>
                  )}
                  {/* Task Notifications */}
                  {(notif.type === 'task_assigned' ||
                    notif.type === 'task_completed' ||
                    notif.type === 'task_update' ||
                    notif.type === 'task_comment') &&
                   (notif.related_id || notif.task_id) && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-indigo-600 font-semibold"
                      data-testid={`notification-view-task-${notif.id}`}
                      onClick={async () => {
                        try {
                          const taskId = notif.related_id || notif.task_id;
                          const response = await axios.get(`${API}/tasks/${taskId}`);
                          if (response.data) {
                            setSelectedTask(response.data);
                            setShowTaskDetail(true);
                            setShowNotifications(false);

                            await axios.post(`${API}/notifications/${notif.id}/read`);
                            fetchNotifications();
                          }
                        } catch (err) {
                          console.error('Error opening task:', err);
                          toast.error('Failed to load task');
                        }
                      }}
                    >
                      View Task
                    </Button>
                  )}
                  {/* Urgent Waitlist Notifications */}
                  {(notif.type === 'urgent_waitlist' || notif.type === 'waitlist_removal') &&
                   notif.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-red-600 font-semibold"
                      data-testid={`notification-waitlist-${notif.id}`}
                      onClick={async () => {
                        try {
                          setActiveTab('waitlist');
                          setShowNotifications(false);

                          const response = await axios.get(`${API}/clients/${notif.client_id}`);
                          if (response.data) {
                            setPreviewClient(response.data);
                            setShowClientPreview(true);
                            toast.info(`Viewing ${notif.type === 'urgent_waitlist' ? 'urgent' : ''} waitlist client: ${response.data.full_name}`);
                          }

                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch (err) {
                          console.error('Error opening waitlist client:', err);
                          toast.error('Failed to load client');
                        }
                      }}
                    >
                      {notif.type === 'urgent_waitlist' ? 'View Urgent Client' : 'View Waitlist Client'}
                    </Button>
                  )}
                  {/* Document Completed/Signature Notifications */}
                  {(notif.type === 'document_completed' || notif.type === 'document_signature') &&
                   (notif.document_id || notif.related_id) && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-purple-600 font-semibold"
                      data-testid={`notification-view-document-${notif.id}`}
                      onClick={async () => {
                        try {
                          const docId = notif.document_id || notif.related_id;
                          const response = await axios.get(`${API}/notes/${docId}`);
                          if (response.data) {
                            setSelectedNote(response.data);
                            setShowNoteDetail(true);
                            setShowNotifications(false);

                            await axios.post(`${API}/notifications/${notif.id}/read`);
                            fetchNotifications();
                          }
                        } catch (err) {
                          console.error('Error opening document:', err);
                          toast.error('Failed to load document');
                        }
                      }}
                    >
                      View Document
                    </Button>
                  )}

                  {/* Med Referral Info Request / Reply / Reminder Notifications */}
                  {(notif.type === 'med_referral_info_request' || notif.type === 'med_referral_updated' || notif.type === 'med_referral_info_reminder') && notif.reference_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-orange-600 font-semibold"
                      data-testid={`notification-med-referral-${notif.id}`}
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/medication-referral-requests`);
                          const referral = response.data.find(r => r.id === notif.reference_id);
                          if (referral) {
                            setSelectedReferralForDetails(referral);
                            setShowNotifications(false);
                          } else {
                            toast.error('Referral not found');
                          }

                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                        } catch (err) {
                          console.error('Error opening referral:', err);
                          toast.error('Failed to load referral');
                        }
                      }}
                    >
                      {notif.type === 'med_referral_updated' ? 'View Referral' : <>View &amp; Respond</>}
                    </Button>
                  )}
                  {/* Note Needs Corrections Notifications */}
                  {notif.type === 'note_needs_corrections' && (notif.note_id || notif.reference_id) && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-orange-600 font-semibold"
                      data-testid={`open-correction-note-${notif.id}`}
                      onClick={async () => {
                        try {
                          const noteId = notif.note_id || notif.reference_id;
                          const noteResp = await axios.get(`${API}/notes/${noteId}`);
                          const note = noteResp.data;
                          if (note.client_id) {
                            const clientResp = await axios.get(`${API}/clients/${note.client_id}`);
                            setSelectedClient(clientResp.data);
                            setShowClientDetail(true);
                          }
                          setSelectedNote(note);
                          setShowNoteDetail(true);
                          setShowNotifications(false);
                          await axios.post(`${API}/notifications/${notif.id}/read`);
                          fetchNotifications();
                          toast.info('Opening note for corrections');
                        } catch (err) {
                          console.error('Error opening note:', err);
                          toast.error('Failed to open note');
                        }
                      }}
                    >
                      Open Note
                    </Button>
                  )}
                  {/* New Client Assigned Notifications */}
                  {notif.type === 'new_client_assigned' && notif.client_id && (
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 h-auto mt-1 text-green-600 font-semibold"
                      data-testid={`notification-new-client-${notif.id}`}
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/clients/${notif.client_id}`);
                          if (response.data) {
                            setSelectedClient(response.data);
                            setShowClientDetail(true);
                            setActiveTab('clients');
                            setShowNotifications(false);

                            await axios.post(`${API}/notifications/${notif.id}/read`);
                            fetchNotifications();
                          }
                        } catch (err) {
                          console.error('Error opening client:', err);
                          toast.error('Failed to load client');
                        }
                      }}
                    >
                      View Client
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <QAFindingResponseDialog
        open={!!qaFindingRef}
        onOpenChange={(o) => { if (!o) setQaFindingRef(null); }}
        reviewId={qaFindingRef?.reviewId}
        sectionId={qaFindingRef?.sectionId}
      />
    </div>
  );
};

/**
 * React.memo wraps the bell so it skips re-renders triggered by unrelated
 * Dashboard state updates (tab switches, dialog opens, etc.). It will
 * still re-render whenever ``notifications``, ``unreadCount``, or
 * ``showNotifications`` change — which is correct.
 *
 * Setters from useState (setShowNotifications, setEditingAppointment, ...)
 * are stable references, so they don't bust memo equality.
 */
export const NotificationBell = React.memo(NotificationBellComponent);

export default NotificationBell;
